import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/layout/PageHero';
import {
  useAgentRun,
  useCancelPredicateSafetyWarning,
  useDispatchDetail,
  useSelectPredicate,
} from '@/features/predicate/hooks';
import type { AgentRunDetail } from '@/features/predicate/api';
import { useIsAdmin } from '@/features/auth/hooks';
import { formatDuration, formatServerDateShort, parseServerDate } from '@/lib/dates';
import { PredicateResultRenderer } from '@/features/predicate/components/PredicateResultRenderer';
import { PredicateStaleBanner } from '@/features/predicate/components/PredicateStaleBanner';
import { PredicateProvenanceCard } from '@/features/predicate/components/PredicateProvenanceCard';
import { isIvdRun } from '@/features/predicate/result-types';
import { DrugFinderResult } from '@/features/predicate/components/drug/DrugFinderResult';
import {
  DRUG_FINDER_RESULT_HERO,
  DRUG_FINDER_WORKFLOW_STEPS,
  DRUG_RESULT_COPY,
  isDrugFinderKind,
} from '@/features/predicate/components/drug/copy';
import { finderRunLabel, usPathwayLabel } from '@/features/predicate/finder-labels';
import { friendlyErrorMessage } from '@/features/predicate/failure-copy';
import { AgentRunCard } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { DispatchTabsShell } from '@/features/agent-dispatch/DispatchTabsShell';
import { QueuedBanner } from '@/features/agent-dispatch/QueuedBanner';
import { FailedBanner } from '@/features/agent-dispatch/FailedBanner';
import { countryLabel } from '@/features/agent-dispatch/countryLabel';
import type { DispatchData } from '@/features/agent-dispatch/types';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';

/**
 * Predicate Finder — result page.
 *
 * URL: /predicate-finder-result?run_id=<n>
 *
 * Polls the run every 3s (via useAgentRun) until status is completed/failed.
 * Shows a status card while running; renders the predicate analysis when
 * completed; shows the error reason on failure. Download Latest Report
 * button when a PDF is available.
 *
 * Polling stops automatically once a terminal state is reached.
 */

/**
 * URL shape: ``?dispatch_group_id=<uuid>`` (new — multi-country dispatch
 * groups) OR ``?run_id=<n>`` (legacy single-run; 409 in-flight conflicts
 * still navigate this way). At least one of the two must be present.
 */
const searchSchema = z.object({
  dispatch_group_id: z.string().min(1).optional().catch(undefined),
  run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

const PREDICATE_WORKFLOW_STEPS = [
  'Extract product specifications',
  'Query regulatory databases',
  'Extract product codes & indications',
  'Build predicate pool (10-30 devices)',
  'Score predicate strength (1-5)',
  'Trace predicate chains',
  'Generate final recommendations',
] as const;

export const Route = createFileRoute('/predicate-finder-result')({
  validateSearch: searchSchema,
  component: PredicateFinderResultPage,
});

function PredicateFinderResultPage() {
  const search = Route.useSearch();
  const groupId = search.dispatch_group_id;
  const runId = search.run_id;
  // Same queries the views below make (shared cache keys), read here only to
  // title the page by finder: the group knows its kind before a run loads.
  const { data: groupData } = useDispatchDetail(groupId ?? null);
  const { data: runData } = useAgentRun(groupId ? null : (runId ?? null));
  const kind = groupData?.runs[0]?.agent_kind ?? runData?.agent_kind ?? null;
  const drugHero = isDrugFinderKind(kind) ? DRUG_FINDER_RESULT_HERO[kind] : null;

  return (
    <>
      <PageHero
        title={drugHero?.title ?? 'Existing Products Analysis'}
        subtitle={
          drugHero?.subtitle
          ?? "Scored matches to existing devices — US FDA 510(k) predicates or EU MDR clinical comparators — with equivalence rationale, grounded in the target market's official device database via the REGARA AI agent."
        }
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-7 pb-20">
        {/* Group dispatch path — new multi-country aware view. */}
        {groupId && <DispatchTabsView groupId={groupId} />}

        {/* Single-run path — covers two cases:
              1. Legacy ``?run_id=N`` bookmarks + 409 conflict redirects
              2. "View Running" button which only knows the in-flight
                 run_id (not the dispatch group). We fetch the run, and
                 if it has a ``dispatch_group_id``, auto-promote to the
                 tabbed multi-country view. */}
        {!groupId && runId !== undefined && <RunOrGroupAutoView runId={runId} />}

        {/* Neither param present. */}
        {!groupId && runId === undefined && <MissingRunIdCard />}
      </main>
      <ScrollToTopButton />
    </>
  );
}


/**
 * Loads a single run by id, then decides: if the run belongs to a
 * dispatch group, render the multi-country tabbed view. Otherwise
 * render the run inline as before.
 *
 * This is the bridge for the "View Running" button (which only has the
 * in-flight run_id) and for any other entry point that doesn't know
 * about ``dispatch_group_id`` directly.
 */
function RunOrGroupAutoView({ runId }: { runId: number }) {
  const { data: run, isLoading, isError } = useAgentRun(runId);

  if (isLoading) {
    return <RunLoadingSkeleton label="Loading analysis" />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  if (run.dispatch_group_id) {
    // ``initialRunId`` opens the tab shell ON the run the URL targeted, not
    // the first sibling (same fix as the strategy result page).
    return (
      <DispatchTabsView groupId={run.dispatch_group_id} initialRunId={run.id} />
    );
  }
  return <SingleRunView runId={runId} />;
}


/**
 * Multi-country view. Reads every sibling run in the dispatch group and
 * renders one tab per country. When the dispatch has a single child
 * (today's single-US case), tabs are hidden and the single run renders
 * inline — visually identical to the legacy single-run view.
 */
function DispatchTabsView({
  groupId,
  initialRunId,
}: {
  groupId: string;
  initialRunId?: number | null;
}) {
  const { data, isLoading, isError } = useDispatchDetail(groupId);
  const shellData: DispatchData | undefined = data
    ? { dispatch_group_id: data.dispatch_group_id, runs: data.runs }
    : undefined;
  return (
    <DispatchTabsShell
      data={shellData}
      isLoading={isLoading}
      isError={isError}
      initialRunId={initialRunId}
      renderRunMeta={(runId) => <RunMetaCard runId={runId} />}
      renderSingleRun={(runId) => <SingleRunView runId={runId} />}
      renderRunBody={(runId, country) => (
        <RunBodyOnly runId={runId} country={country} />
      )}
    />
  );
}


/** Just the AgentRunCard (Run #NNNN header) for a single run id. */
function RunMetaCard({ runId }: { runId: number }) {
  const { data: run } = useAgentRun(runId);
  if (!run) {
    return <RunLoadingSkeleton label="Loading analysis" />;
  }
  return <AgentRunCard run={run} agentLabel={finderRunLabel(run.agent_kind)} />;
}


/** Per-tab body — status panel + completed content + disclaimer.
 * Country name is folded into the inner section titles to avoid the
 * "Analysis for / Analysis in Progress" double-heading redundancy. */
function RunBodyOnly({
  runId,
  country,
}: {
  runId: number;
  country: string | null;
}) {
  const { data: run, isLoading, isError } = useAgentRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton label="Loading" />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load this country's run. Please refresh." />;
  }
  return (
    <>
      <RunStateBody run={run} country={country} />
      {run.status === 'completed' && <ResultDisclaimer />}
    </>
  );
}


function SingleRunView({ runId }: { runId: number }) {
  const { data: run, isLoading, isError } = useAgentRun(runId);

  if (isLoading) {
    return <RunLoadingSkeleton label="Loading analysis" />;
  }
  if (isError) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  if (!run) {
    return null;
  }
  return (
    <>
      <AgentRunCard run={run} agentLabel={finderRunLabel(run.agent_kind)} />
      <RunStateBody run={run} />
      {run.status === 'completed' && <ResultDisclaimer />}
    </>
  );
}

function RunStateBody({
  run,
  country,
}: {
  run: AgentRunDetail;
  country?: string | null;
}) {
  // Country is optional so the legacy single-run path (no group) still works.
  if (run.status === 'queued') {
    return <QueuedBanner country={country} />;
  }

  if (run.status === 'running') {
    return (
      <AgentPollingPanel
        startedAt={run.started_at}
        workflowSteps={
          isDrugFinderKind(run.agent_kind)
            ? DRUG_FINDER_WORKFLOW_STEPS[run.agent_kind]
            : PREDICATE_WORKFLOW_STEPS
        }
        stepSeconds={11}
        title={
          country ? (
            <>
              Analysis in Progress
              <span className="block mt-1.5 font-sans text-electric text-[22px] font-bold tracking-tight">
                {countryLabel(country)}
              </span>
            </>
          ) : (
            'Analysis in Progress'
          )
        }
        subtitle="Searching regulatory databases via the REGARA AI agent."
      />
    );
  }

  if (run.status === 'failed') {
    return (
      <FailedBanner
        country={country}
        message={friendlyErrorMessage(run.error_message, country, run.error_code)}
      />
    );
  }

  return <CompletedSection run={run} country={country} />;
}


function CompletedSection({
  run,
  country,
}: {
  run: AgentRunDetail;
  country?: string | null;
}) {
  // Primary-predicate selection — the submitter's call, recorded on the
  // run (audited) and consumed by the dossier as predicates[0].
  const isAdmin = useIsAdmin();
  const selectPredicate = useSelectPredicate(
    run.id,
    isDrugFinderKind(run.agent_kind) ? DRUG_RESULT_COPY.pick[run.agent_kind].noun : 'predicate',
  );
  const cancelSafety = useCancelPredicateSafetyWarning();
  const startedAt = parseServerDate(run.started_at);
  const completedAt = parseServerDate(run.completed_at);
  const durationSec =
    startedAt && completedAt
      ? Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)
      : null;

  return (
    <>
      {/* Standalone/legacy predicate runs have no product (product_id is
          nullable, unlike strategy runs) — no product, no freshness to check,
          and Number(null)=0 would hammer a doomed /products/0 availability
          query on every visit. Every finder kind tracks freshness against its
          own runs (the device predicate, Reference Listed Drug and Reference
          Product finders each have a materiality family). */}
      {run.product_id != null && (
        <PredicateStaleBanner
          productId={Number(run.product_id)}
          runId={run.id}
          runCompletedAt={run.completed_at}
          // The run row's own country first — the collapsed single-run view
          // passes no tab country, and the banner scopes per market.
          country={run.country ?? country ?? null}
          analysisName={
            isDrugFinderKind(run.agent_kind) ? DRUG_RESULT_COPY.staleAnalysisName[run.agent_kind] : null
          }
        />
      )}
      <div className="mb-[22px] py-[18px] px-[22px] bg-state-success-bg border border-state-success-border rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle className="size-6 text-state-success-accent shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="m-0 mb-1.5 text-[17px] font-bold text-state-success-text">
              Analysis Complete
              {country && (
                <span className="block mt-1 text-navy text-[24px] font-bold tracking-tight">
                  {countryLabel(country)}
                </span>
              )}
            </h3>
            <p className="text-sm leading-[1.55] text-state-success-text/90 m-0">
              Completed {formatServerDateShort(run.completed_at)}
              {durationSec !== null && ` · ${formatDuration(durationSec)}`}
            </p>
          </div>
        </div>
      </div>

      {run.agent_kind === 'predicate' && (
        <PredicateProvenanceCard
          productName={run.product_name}
          productType={run.product_type}
          generatedAt={run.completed_at}
          generatedBy={run.created_by_name}
          country={run.country ?? country ?? null}
          isIvd={isIvdRun(run.output_text, run.submission_type)}
        />
      )}

      {run.agent_kind === 'predicate' && (
        <PredicateResultRenderer
          outputText={run.output_text}
          runId={run.id}
          selectedKNumber={run.selected_k_number}
          candidateSafety={run.candidate_safety ?? null}
          onSelectPredicate={(k, ack) =>
            selectPredicate.mutate(ack ? { kNumber: k, context: ack } : k)
          }
          onCancelSafety={(k, context) =>
            cancelSafety.mutate({ runId: run.id, kNumber: k, context })
          }
          canSelect={isAdmin}
          selectPending={selectPredicate.isPending}
          verificationWarnings={run.verification_warnings}
        />
      )}

      {isDrugFinderKind(run.agent_kind) && (
        <PredicateProvenanceCard
          productName={run.product_name}
          productType={run.product_type}
          generatedAt={run.completed_at}
          generatedBy={run.created_by_name}
          country={run.country ?? country ?? null}
          heading={DRUG_RESULT_COPY.provenanceHeading[run.agent_kind]}
          pathwayLabel={usPathwayLabel(run.submission_type)}
        />
      )}

      {isDrugFinderKind(run.agent_kind) && (
        <DrugFinderResult
          kind={run.agent_kind}
          outputText={run.output_text}
          runId={run.id}
          verificationWarnings={run.verification_warnings}
          selectedNumber={run.selected_k_number}
          candidateSafety={run.candidate_safety ?? null}
          onSelect={(applicationNumber, ack) =>
            selectPredicate.mutate(ack ? { kNumber: applicationNumber, context: ack } : applicationNumber)
          }
          onCancelSafety={(applicationNumber, context) =>
            cancelSafety.mutate({ runId: run.id, kNumber: applicationNumber, context })
          }
          canSelect={isAdmin}
          selectPending={selectPredicate.isPending}
        />
      )}
    </>
  );
}

function MissingRunIdCard() {
  return (
    <div className="bg-state-warning-bg border border-state-warning-border rounded-2xl p-8 text-state-warning-text">
      <div className="font-semibold text-lg">No run specified</div>
      <p className="text-sm mt-1">
        This URL is missing a <code>?run_id=</code> parameter.
      </p>
      <Button variant="outline" asChild className="mt-4">
        <a href="/predicate-finder">← Back to product list</a>
      </Button>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-8 text-state-error-text">
      {message}
    </div>
  );
}

function ResultDisclaimer() {
  return (
    <footer className="mt-7 py-[18px] px-[22px] bg-state-warning-bg-soft border border-state-warning-border rounded-xl text-[#713f12] text-base leading-[1.6]">
      <strong className="block text-xs font-bold uppercase tracking-[0.22em] text-state-warning-text mb-1.5">
        Disclaimer.
      </strong>
      This is REGARA AI-generated decision-support, not legal or regulatory advice.
      Regulatory decisions rest with the relevant authority. Review every match with a qualified
      regulatory professional before relying on it.
    </footer>
  );
}


